import { GroupType } from '@kcftech/prometheus-client';
/**
 * Type to represent a generic HierarchyChild since the API returns different hierarchy types.
 */
export type GenericHierarchyChild = Readonly<{
    HierarchyId: string;
    HierarchyName: string;
    HierarchyAbbreviation?: string | undefined;
    HierarchyType: GroupType;
    HierarchySuffix?: string | undefined;
}>;

/**
 * If there is only one selected root group, returns the hierarchy starting
 * below the root group. Else, returns the hierarchy starting at any of the
 * supplied root groups. If the hierarchy does not contain any of the supplied
 * root groups, the entire hierarchy will be returned.
 *
 * @param hierarchy The indicator hierarchy.
 * @param rootGroupIds The root groups used for truncating. (this is a list only
 * because there may be multiple contextual groups (imagine selecting multiple
 * groups in the tree), but there should never be 2 selections here from the
 * same tree slice (i.e. no parent of another))
 */
export const excludePreRootGroupFromHierarchy = <T extends { HierarchyId: string }>(
    hierarchy: T[],
    rootGroupIds: string[],
    includeRootInHierarchy: boolean,
): T[] => {
    if (hierarchy.length <= 1) {
        return hierarchy;
    }

    const rootIndex = hierarchy.findIndex(
        (h) => h.HierarchyId && rootGroupIds.includes(h.HierarchyId),
    );

    const startingIndex = includeRootInHierarchy ? rootIndex : rootIndex + 1;
    return hierarchy.filter((_, i) => i >= startingIndex);
};

/**
 * Builds a hierarchy string, excluding the first element (the system)
 *
 * @param hierarchy the hierarchy array.
 * @param useFullName if true, uses the full name; if false, uses the abbreviation
 * @param showHierarchySuffix if true, shows the suffix from the hierarchy for each element (if available)
 */
export const buildHierarchy = (
    hierarchy: Omit<GenericHierarchyChild, 'HierarchyType'>[],
    useFullName = false,
    showHierarchySuffix = false,
): string | undefined => {
    if (hierarchy.length === 0) {
        return undefined;
    }
    return hierarchy
        .map(
            (h) =>
                `${useFullName ? h.HierarchyName : h.HierarchyAbbreviation}${
                    showHierarchySuffix && h.HierarchySuffix
                        ? ` (${h.HierarchySuffix})`
                        : ''
                }`,
        )
        .join(' / ');
};

/**
 * Builds the indicator tooltip, excluding the system from the hierarchy
 * @param hierarchy The indicator's hierarchy
 * @param prefix An optional prefix to add to the beginning of the hierarchy (ex: Corporation or Location).
 * @param suffix An optional suffix to add to the end of the hierarchy (ex: an Indicator name).
 * @param useFullName If true, uses the full name; if false, uses the abbreviation.
 */
export const buildTooltip = (
    hierarchy: Omit<GenericHierarchyChild, 'HierarchyType'>[],
    prefix?: string,
    suffix?: string,
    useFullName = true,
) => {
    const formattedHierarchy = buildHierarchy(hierarchy, useFullName);

    if (formattedHierarchy && suffix && prefix) {
        return `${prefix} / ${formattedHierarchy} / ${suffix}`;
    }
    if (formattedHierarchy && suffix) {
        return `${formattedHierarchy} / ${suffix}`;
    }
    if (formattedHierarchy && prefix) {
        return `${prefix} / ${formattedHierarchy}`;
    }
    return `${prefix ?? ''}${formattedHierarchy ?? ''}${suffix ?? ''}`;
};

/**
 * Gets the asset from the indicator hierarchy.
 * For data platform indicators, the asset is always the parent of the parent of the item
 * e.g. Group > Subgroup > Asset      > MP
 *                         length - 2   length - 1
 */
export const findDataPlatformAssetInIndicatorHierarchy = (
    hierarchy: Omit<GenericHierarchyChild, 'HierarchyType'>[] | undefined | null,
) => (hierarchy ? hierarchy[hierarchy.length - 2] : undefined);

/**
 * Searches supplied hierarchy for a asset. If asset is not found, `undefined` is returned.
 *
 * @param hierarchy Indicator hierarchy to search for asset.
 */
export const findAssetInHierarchy = (
    hierarchy: GenericHierarchyChild[] | undefined | null,
): GenericHierarchyChild | undefined => findInHierarchy(hierarchy, GroupType.Asset);

/**
 * Searches supplied hierarchy for a group of the specified type.
 * If group is not found, `undefined` is returned.
 *
 * @param hierarchy Hierarchy to search for group.
 * @param groupType The type of group to search for.
 * @param last If true, searches for the last group of the specified type in the hierarchy.
 */
export const findInHierarchy = (
    hierarchy: GenericHierarchyChild[] | undefined | null,
    groupType: GroupType,
    last: boolean = false,
) => {
    if (!hierarchy || !Array.isArray(hierarchy)) {
        return undefined;
    }

    const findFn = last ? hierarchy.findLast : hierarchy.find;

    return findFn.call(hierarchy, (h) => h.HierarchyType === groupType);
};

/**
 *
 * @param hierarchy
 * @param excludedGroupIds groupIds to remove from the hierarchy.
 * In most cases, this will be in the front of the hierarchy and we want to remove them because they are not necessary and take up space
 * @returns filtered hierarchy without any of the groups to be removed
 */
export const buildTooltipExcludingGroups = (
    hierarchy: GenericHierarchyChild[],
    excludedGroupIds: string[],
    suffix?: string,
    prefix?: string,
) => {
    const formattedHierarchy = hierarchy.filter(
        (h) => h.HierarchyId && !excludedGroupIds.includes(h.HierarchyId),
    );
    return buildTooltip(formattedHierarchy, prefix, suffix);
};

export const hierarchyContainsGroupId = (
    hierarchy: Omit<GenericHierarchyChild, 'HierarchyType'>[],
    groupId: string,
) => hierarchy.some(({ HierarchyId }) => HierarchyId === groupId);
